import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export const WINDOWS = [[7, "7 días"], [30, "30 días"], [90, "90 días"], [0, "Todo"]];
export const SOURCE_LABEL = { campaign: "Campaña", hub: "Hub", direct: "Directo", referral: "Otra web" };
export const SOURCE_KLASS = {
  campaign: "bg-terracotta/15 text-terracotta", hub: "bg-clay-900 text-white",
  direct: "bg-clay-200 text-clay-800", referral: "bg-amber-100 text-amber-800",
};
export const fmtDay = (d) => { const [, m, dd] = d.split("-"); return `${dd}/${m}`; };

export function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-clay-300 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-clay-600"><Icon size={12}/> {label}</div>
      <div className="font-serif text-3xl mt-1 tabular">{value}</div>
      {sub && <div className="text-[11px] text-clay-600 mt-0.5">{sub}</div>}
    </div>
  );
}

export function WindowSelector({ days, onChange }) {
  return (
    <div className="inline-flex border border-clay-300 bg-white" data-testid="stats-window">
      {WINDOWS.map(([d, label]) => (
        <button key={d} onClick={() => onChange(d)} data-testid={`stats-window-${d}`}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-wider ${days === d ? "bg-clay-900 text-white" : "text-clay-700 hover:bg-clay-100"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

const SERIES_LABEL = { views: "Visitas", visitors: "Únicos", reserve_opens: "Clic Reserve", holds: "Retenidas" };

export function DailyChart({ daily, height = 220 }) {
  return (
    <div className="border border-clay-300 bg-white p-4" data-testid="stats-daily-chart">
      <div className="smallcaps mb-3">Visitas por día</div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#e7e0d6"/>
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: "#7a6f63" }} interval="preserveStartEnd" minTickGap={24}/>
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#7a6f63" }}/>
            <Tooltip labelFormatter={fmtDay} formatter={(v, n) => [v, SERIES_LABEL[n] || n]}
              contentStyle={{ fontSize: 12, border: "1px solid #d9d0c3", borderRadius: 0 }}/>
            <Bar dataKey="views" fill="#2f2a25" radius={[2, 2, 0, 0]}/>
            <Bar dataKey="reserve_opens" fill="#c4643c" radius={[2, 2, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 text-[11px] text-clay-600 mt-2">
        <span><span className="inline-block w-2.5 h-2.5 bg-clay-900 mr-1 align-middle"/>Visitas</span>
        <span><span className="inline-block w-2.5 h-2.5 bg-terracotta mr-1 align-middle"/>Clic en Reserve</span>
      </div>
    </div>
  );
}

export function SourcesTable({ sources }) {
  return (
    <div className="border border-clay-300 bg-white" data-testid="stats-sources">
      <div className="smallcaps px-4 pt-4 pb-2">Origen del tráfico</div>
      {sources.length === 0 ? <div className="px-4 pb-4 text-sm text-clay-600">Sin datos en este periodo.</div> : (
        <table className="w-full text-[12px]">
          <thead className="text-clay-500 text-left"><tr><th className="px-4 py-1 font-medium">Origen</th><th className="px-4 py-1 font-medium">Detalle</th><th className="px-4 py-1 font-medium text-right">Visitas</th><th className="px-4 py-1 font-medium text-right">Únicos</th><th className="px-4 py-1 font-medium text-right">Reserve</th></tr></thead>
          <tbody>
            {sources.map((s) => (
              <tr key={`${s.source}-${s.detail}`} className="border-t border-clay-100">
                <td className="px-4 py-1.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${SOURCE_KLASS[s.source]}`}>{SOURCE_LABEL[s.source]}</span></td>
                <td className="px-4 py-1.5 text-clay-800 truncate max-w-[200px]">{s.detail || "—"}</td>
                <td className="px-4 py-1.5 text-right tabular">{s.views}</td>
                <td className="px-4 py-1.5 text-right tabular">{s.visitors}</td>
                <td className="px-4 py-1.5 text-right tabular">{s.reserve_opens}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CampaignsTable({ campaigns }) {
  return (
    <div className="border border-clay-300 bg-white" data-testid="stats-campaigns">
      <div className="smallcaps px-4 pt-4 pb-2">Campañas (utm_campaign)</div>
      {campaigns.length === 0 ? (
        <div className="px-4 pb-4 text-sm text-clay-600">
          Ninguna visita con <code>utm_campaign</code> en este periodo. Añade <code>?utm_source=facebook&amp;utm_campaign=nombre</code> a los enlaces de tus anuncios.
        </div>
      ) : (
        <table className="w-full text-[12px]">
          <thead className="text-clay-500 text-left"><tr><th className="px-4 py-1 font-medium">Campaña</th><th className="px-4 py-1 font-medium">Fuente</th><th className="px-4 py-1 font-medium text-right">Visitas</th><th className="px-4 py-1 font-medium text-right">Retenidas</th><th className="px-4 py-1 font-medium text-right">Pagadas</th></tr></thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={`${c.campaign}-${c.source}`} className="border-t border-clay-100">
                <td className="px-4 py-1.5 text-clay-900 font-medium truncate max-w-[200px]">{c.campaign}</td>
                <td className="px-4 py-1.5 text-clay-700">{c.source || "—"}</td>
                <td className="px-4 py-1.5 text-right tabular">{c.views}</td>
                <td className="px-4 py-1.5 text-right tabular">{c.holds}</td>
                <td className="px-4 py-1.5 text-right tabular">{c.paid}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
